import type { Incident, IncidentId } from '../realtime/contracts';
import { ObservableStore } from './observableStore';

export type IncidentDataState = 'loading' | 'loaded' | 'failed';

export type IncidentsSnapshot = {
  readonly dataState: IncidentDataState;
  readonly incidents: readonly Incident[];
  readonly incidentsById: ReadonlyMap<IncidentId, Incident>;
};

const EMPTY: IncidentsSnapshot = {
  dataState: 'loading',
  incidents: [],
  incidentsById: new Map(),
};

function byCreatedTimeDescending(left: Incident, right: Incident): number {
  return Date.parse(right.createdTimeUtc) - Date.parse(left.createdTimeUtc);
}

export class IncidentStore extends ObservableStore<IncidentsSnapshot> {
  public constructor() {
    super(EMPTY);
  }

  public static get emptySnapshot(): IncidentsSnapshot {
    return EMPTY;
  }

  public getById(incidentId: IncidentId): Incident | null {
    return this.getSnapshot().incidentsById.get(incidentId) ?? null;
  }

  public upsert(incident: Incident): void {
    const { incidentsById } = this.getSnapshot();
    const updated = new Map(incidentsById);
    updated.set(incident.id, incident);

    this.publishIncidents(updated);
  }

  public replaceAll(incidents: readonly Incident[], keep: readonly Incident[] = []): void {
    const updated = new Map<IncidentId, Incident>();
    for (const incident of [...incidents, ...keep]) {
      updated.set(incident.id, incident);
    }

    this.publishIncidents(updated);
  }

  public markFailed(): void {
    this.publish({ dataState: 'failed' });
  }

  private publishIncidents(incidentsById: ReadonlyMap<IncidentId, Incident>): void {
    this.publish({
      dataState: 'loaded',
      incidentsById,
      incidents: [...incidentsById.values()].sort(byCreatedTimeDescending),
    });
  }
}
